import React, { useEffect, useRef, useState } from "react";
import {
  saveFinalResponse,
  generateUserId,
  getPilotStudyState,
  resolveProlificEntry,
  submitConsent,
} from "./service/api";
import { retryApiCall } from "./utils/retry";
import { addToSyncQueue, getSyncStatus } from "./utils/syncQueue";
import { startBackgroundSync, stopBackgroundSync, forceSyncAll } from "./utils/backgroundSync";
import ConsentForm from "./components/ConsentForm";
import DemographicsForm from "./components/DemographicsForm";
import MovieSelection from "./components/MovieSelection";
import WMTIntro from "./components/WMTIntro";
import WMTPractice from "./components/WMTPractice";
import WMTTest from "./components/WMTTest";
// import PreferenceElicitationv2 from "./components/PreferenceElicitationv2";
// import PreferenceWeighting from "./components/PreferenceWeighting";
import PairwiseMovieTask from "./components/PairwiseMovieTask";
import CommonBtrtTask from "./components/CommonBtrtTask";
import CommonInformednessTask from "./components/CommonInformednessTask";
import CommonDivaItemTask from "./components/CommonDivaItemTask";
import CommonDivaRankingTask from "./components/CommonDivaRankingTask";
import MlriRecommendationTask from "./components/MlriRecommendationTask";
import FinalComparisonIntro from "./components/FinalComparisonIntro";
import ThankYouPage from "./components/ThankYouPage";
import AccessibilityPanel from "./components/AccessibilityPanel";
import SyncStatusIndicator from "./components/SyncStatusIndicator";
import AlertModal from "./components/AlertModal";
import StudyLaunchError from "./components/StudyLaunchError";
import { getStudyFlow, isPilotStep, PILOT_START_STEP, STUDY_FLOW_V1, STUDY_FLOW_V2 } from "./config/studyFlow";
import { clearAllStudyData } from "./utils/cleanupStorage";
import {
  readProlificEntryFromUrl,
  removeProlificParametersFromAddressBar,
} from "./prolificEntry";
import ReactDOM from "react-dom";
import axe from "@axe-core/react";
import "./components/CommonTasks.css";
// import { DndProvider } from "react-dnd";
// import { HTML5Backend } from "react-dnd-html5-backend";

const DEBUG_MODE = true;
const DEBUG_START_STEP = PILOT_START_STEP;

// React Strict Mode can mount the app more than once in development before the
// first generate-user-id request resolves. Keep one in-flight promise at module
// scope so the whole app reuses the same user id request.
let pendingUserIdPromise = null;

// Debug consent seeding should also happen at most once per generated user id.
const debugConsentSeededUserIds = new Set();

if (process.env.NODE_ENV !== "production") {
  axe(React, ReactDOM, 1000);
}

async function ensureUserId() {
  const storedId = localStorage.getItem("userId");
  if (storedId) {
    return storedId;
  }

  if (!pendingUserIdPromise) {
    pendingUserIdPromise = generateUserId()
      .then((res) => {
        const id = res.data.userId;
        localStorage.setItem("userId", id);
        return id;
      })
      .finally(() => {
        pendingUserIdPromise = null;
      });
  }

  return pendingUserIdPromise;
}

async function seedDebugConsentIfNeeded(userId) {
  if (!(DEBUG_MODE && DEBUG_START_STEP === "attributeWeights")) {
    return;
  }

  if (debugConsentSeededUserIds.has(userId)) {
    return;
  }

  debugConsentSeededUserIds.add(userId);

  try {
    await submitConsent(userId);
    console.log("✅ Seeded consent row for debug user:", userId);
  } catch (err) {
    debugConsentSeededUserIds.delete(userId);
    console.error("❌ Failed to seed consent row:", err.message);
  }
}

async function confirmCompletedState(userId, initialState = null) {
  let state = initialState ?? await getPilotStudyState(userId);
  if (state?.phase !== "complete") throw new Error("Study completion is not persisted");
  if (!state.completion?.confirmed) {
    const syncStatus = getSyncStatus();
    if (syncStatus.hasPendingItems) {
      const syncResult = await forceSyncAll(5);
      if (!syncResult.success) {
        throw new Error("Queued study data could not be persisted");
      }
    }
    await retryApiCall(() => saveFinalResponse(userId));
    state = await getPilotStudyState(userId);
  }
  if (state?.phase !== "complete" || !state.completion?.confirmed) {
    throw new Error("Study completion could not be confirmed");
  }
  return state;
}

function App() {
  const [userId, setUserId] = useState(null);
  const [participantSource, setParticipantSource] = useState(null);
  const [studyFlowVersion, setStudyFlowVersion] = useState(STUDY_FLOW_V1);
  const [currentStep, setCurrentStep] = useState(() => {
    const savedStep = localStorage.getItem("currentStep");
    return isPilotStep(savedStep) ? savedStep : DEBUG_START_STEP;
  });

  const [pilotCompletionPending, setPilotCompletionPending] = useState(false);
  const [completionContext, setCompletionContext] = useState(null);
  const [completionSaveError, setCompletionSaveError] = useState(false);
  const [launchError, setLaunchError] = useState(false);
  const [pairwiseSessionId, setPairwiseSessionId] = useState(null);
  const [commonSessionId, setCommonSessionId] = useState(null);
  const [studyStateLoading, setStudyStateLoading] = useState(true);
  const completionInFlightRef = useRef(false);

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });


  useEffect(() => {
    console.log("CURRENT STEP:", currentStep);
  }, [currentStep]);

  useEffect(() => {
    let isCancelled = false;

    const initialize = async () => {
      let entryResolved = false;
      try {
        const launch = readProlificEntryFromUrl();
        const storedId = localStorage.getItem("userId");
        let id;
        let source;

        if (launch.kind === "prolific") {
          const entry = await resolveProlificEntry(launch.payload);
          id = entry.userId;
          source = entry.source;
          if (storedId && storedId !== id) {
            clearAllStudyData();
            setCurrentStep(DEBUG_START_STEP);
          }
          localStorage.setItem("userId", id);
          removeProlificParametersFromAddressBar();
        } else {
          const entry = await resolveProlificEntry(storedId ? { userId: storedId } : {});
          id = entry.userId || await ensureUserId();
          source = entry.source;
        }
        entryResolved = true;
        if (isCancelled) return;

        setUserId(id);
        setParticipantSource(source);
        await seedDebugConsentIfNeeded(id);
        let studyState = await getPilotStudyState(id);
        if (isCancelled) return;
        const resolvedVersion = studyState?.studyFlowVersion ?? STUDY_FLOW_V1;
        setStudyFlowVersion(resolvedVersion);
        setCurrentStep((savedStep) => getStudyFlow(resolvedVersion).steps.includes(savedStep) ? savedStep : PILOT_START_STEP);
        if (studyState?.sessionId) setPairwiseSessionId(studyState.sessionId);
        if (studyState?.common?.commonSessionId) setCommonSessionId(studyState.common.commonSessionId);

        if (studyState?.phase === "complete") {
          try {
            studyState = await confirmCompletedState(id, studyState);
            if (isCancelled) return;
            setCompletionContext(studyState.completion);
            setCurrentStep("thankYou");
          } catch {
            if (isCancelled) return;
            setCompletionSaveError(true);
            setCurrentStep(studyState.external ? "externalComparisons" : "pairwise");
          }
          return;
        }

        const stepByPhase = {
          common_btrt_in_progress: "commonBtrt",
          common_informedness_in_progress: "commonInformedness",
          common_diva_items_in_progress: "commonDivaItems",
          common_diva_ranking_in_progress: "commonDivaRanking",
          common_complete: "pairwise",
          personalized_pairwise: "pairwise",
          recommendation_comparison: "recommendationComparison",
          external_test_intro: "finalComparisonIntro",
          external_test_in_progress: "externalComparisons",
        };
        // A v2 user with no common session is still in the local, pre-pairwise
        // sequence. Once the common session exists, backend phase is authoritative.
        const hasAuthoritativeRuntime = resolvedVersion !== STUDY_FLOW_V2
          || studyState?.common?.commonSessionId
          || studyState?.sessionId;
        if (hasAuthoritativeRuntime && stepByPhase[studyState?.phase]) {
          const completedRankingReview = resolvedVersion === STUDY_FLOW_V2
            && studyState?.phase === "common_complete"
            && localStorage.getItem("currentStep") === "commonDivaRanking"
            && !studyState?.sessionId;
          setCurrentStep(completedRankingReview ? "commonDivaRanking" : stepByPhase[studyState.phase]);
        }
      } catch {
        if (isCancelled) return;
        if (!entryResolved) {
          setLaunchError(true);
        } else {
          console.error("[ERROR] Error restoring persisted study state");
        }
      } finally {
        if (!isCancelled) setStudyStateLoading(false);
      }
    };

    initialize();

    return () => {
      isCancelled = true;
    };
  }, []);

  const flow = getStudyFlow(studyFlowVersion);

  const handleCommonPhaseChange = (phase, sessionId) => {
    if (sessionId) setCommonSessionId(sessionId);
    const stepByPhase = {
      common_btrt_in_progress: "commonBtrt",
      common_informedness_in_progress: "commonInformedness",
      common_diva_items_in_progress: "commonDivaItems",
      common_diva_ranking_in_progress: "commonDivaRanking",
      common_complete: "pairwise",
    };
    if (stepByPhase[phase]) setCurrentStep(stepByPhase[phase]);
  };

  useEffect(() => {
    if (currentStep) {
      localStorage.setItem("currentStep", currentStep);
    }
  }, [currentStep]);

  // Start background sync only after participant identity has been resolved.
  useEffect(() => {
    if (!userId || launchError) return undefined;
    console.log('[SYNC] Starting background sync service...');
    startBackgroundSync();

    return () => {
      console.log('[SYNC] Stopping background sync service...');
      stopBackgroundSync();
    };
  }, [launchError, userId]);

  const completePilotStudy = async () => {
    if (completionInFlightRef.current) return;
    completionInFlightRef.current = true;
    setPilotCompletionPending(true);
    setCompletionSaveError(false);

    try {
      const beforeCompletion = await getPilotStudyState(userId);
      if (beforeCompletion?.phase === "recommendation_comparison") {
        setCurrentStep("recommendationComparison");
        return;
      }
      const syncStatus = getSyncStatus();

      if (syncStatus.hasPendingItems) {
        console.log(`[SYNC] Attempting to sync ${syncStatus.pendingCount} queued items before completion...`);
        const syncResult = await forceSyncAll(5);

        if (!syncResult.success) {
          throw new Error(`Could not sync ${syncResult.remaining} pending items`);
        }
      }

      if (flow.saveCompletionMarker) {
        await retryApiCall(() => saveFinalResponse(userId));
      }
      const completedState = await confirmCompletedState(userId);
      setCompletionContext(completedState.completion);
      setCurrentStep("thankYou");
    } catch (error) {
      console.error('[ERROR] Failed to save pilot completion marker after retries:', error);
      addToSyncQueue('Final Response', 'saveFinalResponse', userId);
      setCompletionSaveError(true);
    } finally {
      completionInFlightRef.current = false;
      setPilotCompletionPending(false);
    }
  };

  if (launchError) return <StudyLaunchError />;

  return (
    <>
      {DEBUG_MODE && <AccessibilityPanel />}

      {!studyStateLoading && currentStep === "thankYou" && (
        <ThankYouPage completion={completionContext} />
      )}

      {/* Standard centered layout tasks */}
      {!studyStateLoading && currentStep !== "thankYou" && (
        <div
          className={currentStep === "consent" ? "study-shell--consent" : undefined}
          style={{ maxWidth: currentStep === "recommendationComparison" ? "1280px" : currentStep === "commonInformedness" ? "1040px" : "600px", margin: "auto", textAlign: "center" }}
        >
          <header>
            <h1 id="survey-title">Movie Recommendation Study</h1>
          </header>

          <main aria-labelledby="survey-title">
            {/* Step 1: Consent */}
            {currentStep === "consent" && (
              <ConsentForm
                userId={userId}
                participantSource={participantSource}
                onConsent={() => setCurrentStep(flow.next.consent)}
              />
            )}

            {/* Step 2: Demographics */}
            {currentStep === "demographics" && (
              <DemographicsForm
                userId={userId}
                participantSource={participantSource}
                onNext={() => setCurrentStep(flow.next.demographics)}
                onBack={() => setCurrentStep("consent")}
              />
            )}

            {/* Step 3: Movie Selection */}
            {currentStep === "movies" && (
              <MovieSelection
                userId={userId}
                onNext={() => setCurrentStep(flow.next.movies)}
                onBack={() => setCurrentStep("demographics")}
              />
            )}

            {/* Step 4: WMT Introduction */}
            {currentStep === "wmtIntro" && (
              <WMTIntro
                userId={userId}
                onNext={() => setCurrentStep(flow.next.wmtIntro)}
              />
            )}

            {/* Step 5: WMT Practice */}
            {currentStep === "wmtPractice" && (
              <WMTPractice
                userId={userId}
                onNext={() => setCurrentStep(flow.next.wmtPractice)}
              />
            )}

            {/* Step 6: WMT Test */}
            {currentStep === "wmtTest" && (
              <WMTTest
                userId={userId}
                onNext={() => setCurrentStep(flow.next.wmtTest)}
              />
            )}

            {currentStep === "commonBtrt" && (
              <CommonBtrtTask userId={userId} initialCommonSessionId={commonSessionId} onPhaseChange={handleCommonPhaseChange} />
            )}

            {currentStep === "commonInformedness" && commonSessionId && (
              <CommonInformednessTask userId={userId} commonSessionId={commonSessionId} onPhaseChange={handleCommonPhaseChange} />
            )}

            {currentStep === "commonDivaItems" && commonSessionId && (
              <CommonDivaItemTask userId={userId} commonSessionId={commonSessionId} onPhaseChange={handleCommonPhaseChange} />
            )}

            {currentStep === "commonDivaRanking" && commonSessionId && (
              <CommonDivaRankingTask userId={userId} commonSessionId={commonSessionId} onPhaseChange={handleCommonPhaseChange} />
            )}

            {/* {currentStep === "preferences" && (
          <DndProvider backend={HTML5Backend}>
            <PreferenceElicitationv2
              key={Date.now()}
              userId={userId}
              onNext={() => setCurrentStep("attributeWeights")}
            />
          </DndProvider>
        )}

        {currentStep === "attributeWeights" && (
          <PreferenceWeighting
            userId={userId}
            onBack={() => {
              localStorage.setItem("preferenceStep", "4");
              setCurrentStep("preferences");
            }}
            onNext={() => setCurrentStep("pairwise")}
          />
        )} */}

            {currentStep === "pairwise" && (
              pilotCompletionPending ? (
                <p role="status">Saving your pilot completion...</p>
              ) : completionSaveError ? (
                <div role="alert">
                  <p>We couldn’t confirm that all study data were saved. Please keep this page open and try again.</p>
                  <button type="button" className="btn btn-primary" onClick={completePilotStudy}>Try again</button>
                </div>
              ) : (
                <PairwiseMovieTask
                  userId={userId}
                  initialSessionId={pairwiseSessionId}
                  onComplete={() => {}}
                  onPhaseChange={(phase, sessionId) => {
                    setPairwiseSessionId(sessionId);
                    if (phase === "external_test_intro") setCurrentStep("finalComparisonIntro");
                    else if (phase === "external_test_in_progress") setCurrentStep("externalComparisons");
                    else if (phase === "recommendation_comparison") setCurrentStep("recommendationComparison");
                    else if (phase === "complete") completePilotStudy();
                  }}
                  onBack={studyFlowVersion === STUDY_FLOW_V1 ? () => setCurrentStep("wmtTest")
                    : (!pairwiseSessionId && commonSessionId ? () => setCurrentStep("commonDivaRanking") : undefined)}
                />
              )
            )}

            {currentStep === "recommendationComparison" && pairwiseSessionId && (
              <MlriRecommendationTask userId={userId} sessionId={pairwiseSessionId} onComplete={completePilotStudy} />
            )}

            {currentStep === "finalComparisonIntro" && pairwiseSessionId && (
              <FinalComparisonIntro
                userId={userId}
                sessionId={pairwiseSessionId}
                onContinue={() => setCurrentStep("externalComparisons")}
                onComplete={completePilotStudy}
              />
            )}

            {currentStep === "externalComparisons" && pairwiseSessionId && (
              pilotCompletionPending ? (
                <p role="status">Saving your pilot completion...</p>
              ) : completionSaveError ? (
                <div role="alert">
                  <p>We couldn’t confirm that all study data were saved. Please keep this page open and try again.</p>
                  <button type="button" className="btn btn-primary" onClick={completePilotStudy}>
                    Try again
                  </button>
                </div>
              ) : (
                <PairwiseMovieTask
                  mode="external"
                  userId={userId}
                  initialSessionId={pairwiseSessionId}
                  onComplete={completePilotStudy}
                  onPhaseChange={(phase) => {
                    if (phase === "external_test_intro") setCurrentStep("finalComparisonIntro");
                    else if (phase === "recommendation_comparison") setCurrentStep("recommendationComparison");
                    else if (phase === "complete") completePilotStudy();
                  }}
                />
              )
            )}

          </main>
        </div>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />

      {/* Sync Status Indicator */}
      {currentStep !== "thankYou" && (
        <SyncStatusIndicator />
      )}
    </>
  );
}

export default App;
